import React from "react";
import { Service } from "@/types/service";
import { Badge } from "@/components/ui/badge";
import { TestTube, Eye, EyeOff, Star, EditIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isProduction } from "@/utils/environment";
import { Button } from "@/components/ui/button";

interface ServiceTableProps {
  services: Service[];
  onTogglePublished: (id: number, currentStatus: boolean) => void;
  onToggleFeatured: (id: number, currentStatus: boolean) => void;
}

export const ServiceTable = ({ services, onTogglePublished, onToggleFeatured }: ServiceTableProps) => {
  const navigate = useNavigate();
  const isProd = isProduction();

  const handleRowClick = (serviceId: number) => {
    navigate(`/admin/services/edit/${serviceId}`);
  };

  const getCategoryDisplay = (service: Service) => {
    if (service.subcategory_details?.label) {
      const parentLabel = service.subcategory_details.parent?.label;
      if (parentLabel) {
        return `${parentLabel} - ${service.subcategory_details.label}`;
      }
      return service.subcategory_details.label;
    }
    return 'No category';
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Service
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Owner
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Price
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {services.map((service) => (
            <tr 
              key={service.id} 
              className={`${service.test_only ? "bg-orange-50" : ""} hover:bg-gray-50 cursor-pointer transition-colors`}
              onClick={() => handleRowClick(service.id)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    {service.image_url ? (
                      <img 
                        className="h-10 w-10 rounded-full object-cover" 
                        src={service.image_url} 
                        alt={service.name} 
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {service.name?.[0] || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {service.name}
                      {service.test_only && (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                          <TestTube className="w-3 h-3 mr-1" />
                          Test
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {service.id}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {getCategoryDisplay(service)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {service.owner?.display_name || service.owner?.email || 'No owner'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                Ksh {service.price}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-col gap-1">
                  <Badge variant={service.published ? "success" : "secondary"}>
                    {service.published ? "Published" : "Draft"}
                  </Badge>
                  {service.featured && (
                    <Badge variant="default" className="bg-yellow-100 text-yellow-800">
                      <Star className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {!isProd && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            <TestTube className="w-4 h-4 inline mr-1" />
            Development Mode: Test services are visible. In production, test services are automatically hidden from public views.
          </p>
        </div>
      )}
    </div>
  );
};
